import { AggregatedOrderProduct, AggregatedOrderProductSortInfo } from "./type";

const PRODUCT_HASH_SIZE = 9;
const SKU_HASH_SIZE = 9;
const PUNCTUATION_SET = new Set([
    ":",
    ";",
    ",",
    ".",
    "*",
    "/",
    "(",
    ")",
    "[",
    "]",
    "<",
    ">",
    "+",
    "：",
    "；",
    "，",
    "。",
    "（",
    "）",
    "-",
    "【",
    "】",
]);

function isNotEmpty(value?: string | null) {
    return value !== undefined && value !== null && value.length > 0;
}

function stringHashCode(str: string) {
    let hashCode = 0;

    for (let i = 0; i < str.length; i++) {
        hashCode = (Math.imul(31, hashCode) + str.charCodeAt(i)) | 0;
    }

    return hashCode;
}

function toPureString(str: string) {
    let pureString = "";

    for (let i = 0; i < str.length; i++) {
        const char = str.charAt(i);

        if (!PUNCTUATION_SET.has(char)) {
            pureString += char;
        }
    }

    return pureString;
}

function hash(str: string | null | undefined, size: number) {
    if (!isNotEmpty(str)) {
        return 1;
    }

    let hashCode = stringHashCode(toPureString(str));

    if (hashCode === 0) {
        return 1;
    }

    if (hashCode < 0) {
        hashCode = -hashCode;
    }

    return String(hashCode).length > size ? hashCode % Math.pow(10, size) : hashCode;
}

function splitSkuProperties(str: string) {
    const result: string[] = [];
    let startIndex = 0;

    while (startIndex < str.length) {
        while (startIndex < str.length && str.charAt(startIndex) === ",") {
            startIndex++;
        }

        if (startIndex >= str.length) {
            break;
        }

        if (result.length === 1) {
            result.push(str.substring(startIndex));
            break;
        }

        let endIndex = startIndex;

        while (endIndex < str.length && str.charAt(endIndex) !== ",") {
            endIndex++;
        }

        result.push(str.substring(startIndex, endIndex));
        startIndex = endIndex;
    }

    return result;
}

function hashSkuProperties(str: string) {
    if (str.indexOf(",") === -1) {
        return hash(str, SKU_HASH_SIZE);
    }

    const skuPropertyArray = splitSkuProperties(str);

    if (skuPropertyArray.length !== 2) {
        return hash(str, SKU_HASH_SIZE);
    }

    return hash(skuPropertyArray[0], 5) * 10000 + hash(skuPropertyArray[1], 4);
}

function stripLeadingZeros(value: string) {
    const result = value.replace(/^0+/, "");
    return result || "0";
}

function comparePositiveIntegerString(a: string, b: string) {
    const normalizedA = stripLeadingZeros(a);
    const normalizedB = stripLeadingZeros(b);

    if (normalizedA.length !== normalizedB.length) {
        return normalizedA.length - normalizedB.length;
    }

    return normalizedA.localeCompare(normalizedB);
}

function addPositiveIntegerString(a: string, b: string) {
    let result = "";
    let carry = 0;
    let aIndex = a.length - 1;
    let bIndex = b.length - 1;

    while (aIndex >= 0 || bIndex >= 0 || carry) {
        const aDigit = aIndex >= 0 ? Number(a.charAt(aIndex)) : 0;
        const bDigit = bIndex >= 0 ? Number(b.charAt(bIndex)) : 0;
        const sum = aDigit + bDigit + carry;

        result = String(sum % 10) + result;
        carry = Math.floor(sum / 10);
        aIndex--;
        bIndex--;
    }

    return stripLeadingZeros(result);
}

function subtractPositiveIntegerString(a: string, b: string) {
    let result = "";
    let borrow = 0;
    let aIndex = a.length - 1;
    let bIndex = b.length - 1;

    while (aIndex >= 0) {
        const aDigit = Number(a.charAt(aIndex)) - borrow;
        const bDigit = bIndex >= 0 ? Number(b.charAt(bIndex)) : 0;

        if (aDigit < bDigit) {
            result = String(aDigit + 10 - bDigit) + result;
            borrow = 1;
        } else {
            result = String(aDigit - bDigit) + result;
            borrow = 0;
        }

        aIndex--;
        bIndex--;
    }

    return stripLeadingZeros(result);
}

function addIntegerToPositiveIntegerString(value: string, addend: number) {
    if (addend >= 0) {
        return addPositiveIntegerString(value, String(addend));
    }

    const subtrahend = String(Math.abs(addend));

    if (comparePositiveIntegerString(value, subtrahend) >= 0) {
        return subtractPositiveIntegerString(value, subtrahend);
    }

    return "-" + subtractPositiveIntegerString(subtrahend, value);
}

function buildSingleProductBackendSortIndex(productHash: number, skuHash: number) {
    const productPart = `${productHash}${"0".repeat(SKU_HASH_SIZE)}`;

    return addIntegerToPositiveIntegerString(productPart, skuHash);
}

function calcUniqueId(str?: string) {
    if (!isNotEmpty(str)) {
        return null;
    }

    if (str.length === 1) {
        return String(stringHashCode(str));
    }

    const halfLength = Math.floor(str.length / 2);
    const part1 = str.substring(0, halfLength);
    const part2 = str.substring(halfLength);
    const hash1 = Math.abs(stringHashCode(part1));
    const hash2 = stringHashCode(part2);
    const decimalPlaces = hash1 < 922337203 ? 10 : 9;
    const baseValue = `${hash1}${"0".repeat(decimalPlaces)}`;

    return addIntegerToPositiveIntegerString(baseValue, hash2);
}

function getProductId(sortInfo: AggregatedOrderProductSortInfo) {
    if (isNotEmpty(sortInfo.productExternalId)) {
        return sortInfo.productExternalId;
    }

    if (isNotEmpty(sortInfo.productOriginalId)) {
        return sortInfo.productOriginalId;
    }

    return calcUniqueId(sortInfo.title);
}

function getSkuProperties(sortInfo: AggregatedOrderProductSortInfo) {
    return sortInfo.localSkuProperties !== undefined && sortInfo.localSkuProperties !== null
        ? sortInfo.localSkuProperties
        : sortInfo.skuProperties;
}

function getSkuId(sortInfo: AggregatedOrderProductSortInfo) {
    return sortInfo.skuExternalId !== undefined && sortInfo.skuExternalId !== null
        ? sortInfo.skuExternalId
        : sortInfo.skuOriginalId;
}

function getBackendSortIndex(product: AggregatedOrderProduct) {
    const sortInfo: AggregatedOrderProductSortInfo = product.sortInfo || {};
    const skuProperties = getSkuProperties(sortInfo);
    // Align with the single-item branch of backend OrderSortIndexCalculator.
    const skuHash = isNotEmpty(skuProperties)
        ? hashSkuProperties(skuProperties)
        : hash(getSkuId(sortInfo), SKU_HASH_SIZE);

    return buildSingleProductBackendSortIndex(
        hash(getProductId(sortInfo), PRODUCT_HASH_SIZE),
        skuHash + product.quantity,
    );
}

export function compareAggregatedOrderProductByBackendSortIndex(a: AggregatedOrderProduct, b: AggregatedOrderProduct) {
    const aSortIndex = getBackendSortIndex(a);
    const bSortIndex = getBackendSortIndex(b);
    const sortIndexCompareResult = comparePositiveIntegerString(aSortIndex, bSortIndex);

    if (sortIndexCompareResult !== 0) {
        return sortIndexCompareResult;
    }

    return a.id.localeCompare(b.id);
}
